import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { MapPin, Plus, Hospital, Loader2, Navigation } from "lucide-react";
import { toast } from "sonner";
import { locationsApi } from "@/lib/api";

export function LocationsSection({ project, locations, onLocationUpdated }) {
  const projectLocIds = project.location_ids && project.location_ids.length
    ? project.location_ids
    : project.location_id ? [project.location_id] : [];
  const linkedLocs = projectLocIds
    .map((id) => locations.find((l) => l.id === id))
    .filter(Boolean);

  return (
    <section className="rounded-md border border-zinc-800 bg-zinc-900/60 p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <MapPin className="w-4 h-4 text-amber-500" />
          <div className="text-overline text-zinc-400">
            {linkedLocs.length > 1 ? "Locations" : "Location"}
          </div>
          {linkedLocs.length > 1 && (
            <span className="text-xs font-mono text-zinc-500">
              · {linkedLocs.length}
            </span>
          )}
        </div>
        <Link
          to={`/projects/${project.id}`}
          className="text-xs text-zinc-500 hover:text-amber-500 inline-flex items-center gap-1"
        >
          Edit on project
        </Link>
      </div>
      {linkedLocs.length === 0 ? (
        <div className="rounded-sm border border-dashed border-zinc-800 bg-zinc-950/40 p-5 text-center">
          <div className="text-sm text-zinc-400 mb-2">
            No locations set on this project.
          </div>
          <Link
            to={`/projects/${project.id}`}
            className="inline-flex items-center gap-1.5 text-xs text-amber-500 hover:text-amber-400"
          >
            <Plus className="w-3 h-3" /> Add from the project page
          </Link>
        </div>
      ) : (
        <div className="space-y-3">
          {linkedLocs.map((loc) => (
            <LocationCard
              key={loc.id}
              loc={loc}
              onLocationUpdated={onLocationUpdated}
            />
          ))}
        </div>
      )}
    </section>
  );
}

function LocationCard({ loc, onLocationUpdated }) {
  const [findingHospital, setFindingHospital] = useState(false);
  const [parking, setParking] = useState(loc.parking_info || "");
  const [savingParking, setSavingParking] = useState(false);
  const parkingInitial = useRef(loc.parking_info || "");

  // Keep input synced with upstream prop changes (e.g. when reload() runs)
  useEffect(() => {
    setParking(loc.parking_info || "");
    parkingInitial.current = loc.parking_info || "";
  }, [loc.parking_info]);

  const handleFindHospital = async () => {
    if (!loc.address) {
      toast.error("Location needs an address first");
      return;
    }
    setFindingHospital(true);
    try {
      const res = await locationsApi.findHospital(loc.id);
      const n = res.nearest;
      toast.success(
        `Hospital set · ${n.name} (${n.distance_km} km${n.has_er ? " · ER" : ""})`
      );
      onLocationUpdated?.();
    } catch (e) {
      toast.error(
        e?.response?.data?.detail || "No emergency room found nearby"
      );
    } finally {
      setFindingHospital(false);
    }
  };

  const persistParkingIfChanged = async () => {
    if (parking === parkingInitial.current) return;
    setSavingParking(true);
    try {
      await locationsApi.update(loc.id, { parking_info: parking });
      parkingInitial.current = parking;
      onLocationUpdated?.();
    } catch {
      toast.error("Couldn't save parking notes");
    } finally {
      setSavingParking(false);
    }
  };

  return (
    <div
      data-testid={`cs-location-${loc.id}`}
      className="rounded-sm border border-zinc-800 border-l-2 border-l-amber-500 bg-zinc-950 p-4"
    >
      <div className="font-heading text-lg text-zinc-100 mb-2">{loc.name}</div>
      {loc.address && (
        <a
          href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(loc.address)}`}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-start gap-2 text-sm text-zinc-200 hover:text-amber-500 mb-1.5 group"
          data-testid={`builder-location-address-${loc.id}`}
          title="Open in Maps"
        >
          <MapPin className="w-3.5 h-3.5 mt-0.5 text-amber-500/70 shrink-0" />
          <span className="leading-snug">
            {loc.address}
            <Navigation className="inline w-3 h-3 ml-1.5 text-zinc-500 group-hover:text-amber-500" />
          </span>
        </a>
      )}
      <div className="grid grid-cols-1 gap-2 text-xs text-zinc-500 mt-2">
        <div className="flex items-center gap-2">
          <span className="text-overline text-zinc-600 shrink-0 inline-flex items-center gap-1">
            🅿 <span>Parking</span>
          </span>
          <input
            type="text"
            value={parking}
            onChange={(e) => setParking(e.target.value)}
            onBlur={persistParkingIfChanged}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                e.currentTarget.blur();
              }
            }}
            data-testid={`cs-parking-${loc.id}`}
            placeholder="e.g. Free lot on west side, valet at front, street only…"
            className="flex-1 bg-transparent border-0 border-b border-zinc-800 focus:border-amber-500 focus:outline-none text-xs text-zinc-300 placeholder:text-zinc-600 px-0 py-1"
          />
          {savingParking && (
            <Loader2 className="w-3 h-3 text-amber-500 animate-spin shrink-0" />
          )}
        </div>
        <div className="flex items-start gap-2">
          <span className="text-overline text-zinc-600 shrink-0 mt-0.5">
            Hospital
          </span>
          {loc.nearest_hospital ? (
            <div className="flex-1 flex items-start justify-between gap-2">
              <a
                href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(loc.nearest_hospital)}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`builder-hospital-link-${loc.id}`}
                title="Open in Maps"
                className="text-rose-300 hover:text-rose-200 hover:underline inline-flex items-start gap-1"
              >
                <Hospital className="w-3 h-3 mt-0.5 shrink-0" />
                <span>{loc.nearest_hospital}</span>
              </a>
              <button
                type="button"
                onClick={handleFindHospital}
                disabled={findingHospital || !loc.address}
                title="Re-find nearest hospital"
                data-testid={`refind-hospital-${loc.id}`}
                className="shrink-0 text-zinc-600 hover:text-amber-500 transition-colors disabled:opacity-30"
              >
                {findingHospital ? (
                  <Loader2 className="w-3 h-3 animate-spin" />
                ) : (
                  <Hospital className="w-3 h-3" />
                )}
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={handleFindHospital}
              disabled={findingHospital || !loc.address}
              data-testid={`find-hospital-btn-${loc.id}`}
              title={
                !loc.address
                  ? "Location needs an address first"
                  : "Find nearest ER via OpenStreetMap"
              }
              className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs bg-amber-500/10 border border-amber-500/30 text-amber-400 hover:bg-amber-500/20 rounded-sm disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              {findingHospital ? (
                <>
                  <Loader2 className="w-3 h-3 animate-spin" /> Finding…
                </>
              ) : (
                <>
                  <Hospital className="w-3 h-3" /> Auto-find Hospital
                </>
              )}
            </button>
          )}
        </div>
      </div>
      {(loc.contact_name || loc.contact_phone) && (
        <div className="pt-3 mt-3 border-t border-zinc-800/60 flex items-center gap-3 text-xs flex-wrap">
          {loc.contact_name && (
            <span className="text-zinc-400">{loc.contact_name}</span>
          )}
          {loc.contact_phone && (
            <a
              href={`tel:${loc.contact_phone}`}
              className="font-mono text-amber-500 hover:text-amber-400"
              data-testid={`builder-location-phone-${loc.id}`}
            >
              {loc.contact_phone}
            </a>
          )}
        </div>
      )}
    </div>
  );
}
